import React, { useEffect, useState } from "react";
import axios, {create} from "axios";
import {
    LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import "./Analytics.css";
import { saveAs } from "file-saver";

const renderCustomizedLabel = ({ percent, name }) => {
    return `${name} ${(percent * 100).toFixed(0)}%`;
};


const Analytics = () => {

    const [userStats, setUserStats] = useState({ active: 0, inactive: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 12;
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const goToPage = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };


    const [analyticsData, setAnalyticsData] = useState({
        revenueData: [],
        dailySales: [],
        eventsSalesData: [],
        ticketSales: [],
        users: [],
        userStats: { active: 0, inactive: 0 },
        ticketClassRevenue: []
    });
    const [search, setSearch] = useState({
        ticketID: "",
        name: "",
        email: "",
        event: "",
        mobile: "",
        status: "",
        ticketClass: "",
        giftCheck: ""
    });
    const setSearchFilter = (key, value) => {
        setSearch((prev) => ({ ...prev, [key]: value.toLowerCase() }));
    };
    useEffect(() => {
        fetchAnalyticsData();
    }, []);
    useEffect(() => {
        console.log("Ticket Class Revenue Data:", analyticsData.ticketClassRevenue);
    }, [analyticsData.ticketClassRevenue]);
    const filteredUsers = analyticsData.users.filter(user =>
        user.ticketID?.toUpperCase().includes(search.ticketID.toUpperCase()) &&
        user.name?.toLowerCase().includes(search.name) &&
        user.email?.toLowerCase().includes(search.email) &&
        user.event?.toLowerCase().includes(search.event) &&
        user.mobile?.toLowerCase().includes(search.mobile) &&
        user.ticketClass?.toLowerCase().includes(search.ticketClass) &&
        user.status?.toLowerCase().includes(search.status) &&
        (search.giftCheck === "" || (user.giftCheck ? "yes" : "no") === search.giftCheck)
    );

    const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" });

    const sortedUsers = [...filteredUsers].sort((a, b) => {
        if (!sortConfig.key) return 0;

        let valueA = a[sortConfig.key] ?? "";
        let valueB = b[sortConfig.key] ?? "";

        if (!isNaN(valueA) && !isNaN(valueB)) {
            return sortConfig.direction === "asc" ? valueA - valueB : valueB - valueA;
        }

        if (sortConfig.key === "createdAt") {
            return sortConfig.direction === "asc"
                ? new Date(valueA) - new Date(valueB)
                : new Date(valueB) - new Date(valueA);
        }

        valueA = valueA.toString().toLowerCase();
        valueB = valueB.toString().toLowerCase();

        if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
    });

    const currentRecords = sortedUsers.slice(indexOfFirstRecord, indexOfLastRecord);

    const totalPages = Math.ceil(sortedUsers.length / recordsPerPage);

    const requestSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
        }));
    };
    const fetchUserStats = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`);
            const activeUsers = res.data.filter(user => user.role === "user").length;
            const inactiveUsers = res.data.length - activeUsers;

            setUserStats({ active: activeUsers, inactive: inactiveUsers });
        } catch (error) {
            console.error("Error fetching user stats:", error);
        }
    };
    useEffect(() => {
        fetchUserStats();
    }, []);

    const fetchAnalyticsData = async () => {
        try {
            const eventRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/events`);
            if (!eventRes.data || eventRes.data.length === 0) {
                console.error("❌ No events found. Skipping sales data fetch.");
                return;
            }
            let allDailySales = new Map();
            let ticketSales = new Map();
            let usersData = [];
            let revenueData = [];
            let ticketClassRevenue = new Map();
            await Promise.all(eventRes.data.map(async (event) => {
                if (!event._id) {
                    console.warn(`⚠️ Skipping event: Missing _id for ${event.title}`);
                    return;
                }
                try {
                    const ticketRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/tickets/sales/daily/${event._id}`);
                    const ticketBuyersRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/tickets/buyers/${event._id}`);
                    const ticketClassRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/tickets/sales/class/${event._id}`);
                    if (ticketRes.data && ticketRes.data.length > 0) {
                        ticketRes.data.forEach(sale => {
                            const date = sale._id;
                            if (!allDailySales.has(date)) {
                                allDailySales.set(date, { date, tickets: 0, revenue: 0 });
                            }
                            allDailySales.get(date).tickets += sale.tickets || 0;
                            allDailySales.get(date).revenue += sale.revenue || 0;
                        });
                    }
                    if (ticketClassRes.data && ticketClassRes.data.length > 0) {
                        ticketClassRes.data.forEach(sale => {
                            const ticketType = sale._id;
                            if (!ticketSales.has(ticketType)) {
                                ticketSales.set(ticketType, { name: ticketType, tickets: 0 });
                            }
                            ticketSales.get(ticketType).tickets += sale.tickets || 0;
                        });
                    }
                    if (ticketClassRes.data && ticketClassRes.data.length > 0) {
                        ticketClassRes.data.forEach(sale => {
                            const ticketType = sale._id;
                            const ticketCount = sale.tickets || 0;
                            const eventData = eventRes.data.find(ev => ev._id === event._id);
                            const ticketClass = eventData?.priceClasses.find(pc => pc.name === ticketType);
                            const ticketPrice = ticketClass ? ticketClass.price : 0;

                            const revenue = ticketCount * ticketPrice;

                            if (!ticketClassRevenue.has(ticketType)) {
                                ticketClassRevenue.set(ticketType, { name: ticketType, revenue: 0 });
                            }
                            ticketClassRevenue.get(ticketType).revenue += revenue;
                        });
                    }
                    if (ticketBuyersRes.data && ticketBuyersRes.data.length > 0) {
                        usersData = [...usersData, ...ticketBuyersRes.data];
                    }
                    revenueData.push({
                        name: event.title,
                        revenue: ticketRes.data.reduce((sum, sale) => sum + (sale.revenue || 0), 0)
                    });

                } catch (error) {
                    console.error(`❌ Error fetching sales for event ${event._id}:`, error?.response?.data || error.message);
                }
            }));
            const dailySales = Array.from(allDailySales.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
            const ticketSalesData = Array.from(ticketSales.values());

            setAnalyticsData(prev => ({
                ...prev,
                dailySales: dailySales || prev.dailySales,
                revenueData: revenueData || prev.revenueData,
                ticketSales: ticketSalesData || prev.ticketSales,
                users: usersData || prev.users,
                ticketClassRevenue: Array.from(ticketClassRevenue.values()) || prev.ticketClassRevenue,
                userStats: prev.userStats,
                eventsSalesData: prev.eventsSalesData
            }));


        } catch (error) {
            console.error("❌ Error fetching analytics data:", error?.response?.data || error.message);
        }
    };
    const exportTicketBuyersToCSV = () => {
        const csvRows = [
            ["Ticket ID", "Name", "Email", "Mobile", "Event", "Ticket Class", "Total Price", "Gift Check", "Status", "Created On"],
            ...filteredUsers.map(user => [
                user.ticketID || "N/A",
                user.name,
                user.email,
                user.mobile || "N/A",
                user.event,
                user.ticketClass,
                `$${user.totalPrice.toFixed(2)}`,
                user.giftCheck !== undefined ? (user.giftCheck ? "Yes" : "No") : "No",
                user.status || "Pending",
                user.createdAt || "N/A"
            ])
        ];
        const csvContent = csvRows.map(row => row.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        saveAs(blob, "ticket_buyers.csv");
    };
    const eventTicketStats = {};
    sortedUsers.forEach(user => {
        if (!eventTicketStats[user.event]) {
            eventTicketStats[user.event] = { sold: 0, total: user.totalTicketsAvailable || 0 };
        }
        eventTicketStats[user.event].sold += 1;
    });
    const [ticketSalesData, setTicketSalesData] = useState([]);
    useEffect(() => {
        const fetchTicketSales = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/events/ticket-sales`);
                setTicketSalesData(res.data);
            } catch (error) {
                console.error("Error fetching ticket sales:", error);
            }
        };
        fetchTicketSales();
        const interval = setInterval(fetchTicketSales, 10000);
        return () => clearInterval(interval);    }, []);
    return (
        <div className="analytics-container">
            <h1>Event Analytics</h1>
            <div className="chart-section">
                <h2>Ticket Buyers</h2>
                <div className="table-search">
                    <input type="text" placeholder="Search Ticket ID" onChange={(e) => setSearchFilter('ticketID', e.target.value)} />
                    <input type="text" placeholder="Search Name" onChange={(e) => setSearchFilter('name', e.target.value)} />
                    <input type="text" placeholder="Search Email" onChange={(e) => setSearchFilter('email', e.target.value)} />
                    <input type="text" placeholder="Search Event" onChange={(e) => setSearchFilter('event', e.target.value)} />
                    <input type="text" placeholder="Search Mobile" onChange={(e) => setSearchFilter('mobile', e.target.value)} />
                    <input type="text" placeholder="Search Ticket Class" onChange={(e) => setSearchFilter('ticketClass', e.target.value)} />
                    <select onChange={(e) => setSearchFilter('giftCheck', e.target.value.toLowerCase())} defaultValue="">
                        <option value="">All Gift Checks</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                    </select>
                    <select onChange={(e) => setSearchFilter('status', e.target.value)} defaultValue="">
                        <option value="">All Statuses</option>
                        <option value="Ready to Scan">Ready to Scan</option>
                        <option value="Scanned">Scanned</option>
                    </select>
                </div>
                <div className="table-container event-buyers-table">
                    <table className="buyers-table">
                        <thead>
                        <tr>
                            <th onClick={() => requestSort("ticketID")}>Ticket ID</th>
                            <th onClick={() => requestSort("name")}>Name</th>
                            <th onClick={() => requestSort("email")}>Email</th>
                            <th onClick={() => requestSort("mobile")}>Mobile</th>
                            <th onClick={() => requestSort("event")}>Event</th>
                            <th onClick={() => requestSort("ticketClass")}>Ticket Class</th>
                            <th onClick={() => requestSort("totalPrice")}>Total Price</th>
                            <th onClick={() => requestSort("giftCheck")}>Gift Check</th>
                            <th onClick={() => requestSort("status")}>Status</th>
                            <th onClick={() => requestSort("createdAt")}>Created On</th>
                        </tr>
                        </thead>
                        <tbody>
                        {currentRecords.map((user, index) => (
                            <tr key={index} className={
                                user.status === "Scanned"
                                    ? "scanned-ticket"
                                    : user.status === "Expired" || (user.expiresAt && new Date(user.expiresAt) < new Date() && user.status === "Ready to Scan")
                                        ? "expired-ticket"
                                        : "ready-to-scan"
                            }>


                            <td>{user.ticketID || "N/A"}</td>
                                <td>{user.name}</td>
                                <td>{user.email}</td>
                                <td>{user.mobile || "N/A"}</td>
                                <td>{user.event || "Unknown Event"}</td>
                                <td>{user.ticketClass || "Unknown Class"}</td>
                                <td>{user.totalPrice.toFixed(2)} JOD</td>
                                <td>{user.giftCheck ? "Yes" : "No"}</td>
                                <td>
                                    {(user.status === "Expired" || (user.expiresAt && new Date(user.expiresAt) < new Date() && user.status === "Ready to Scan"))
                                        ? "Expired"
                                        : user.status || "Ready to Scan"}
                                </td>

                                <td>{user.createdAt ? new Date(user.createdAt).toLocaleString() : "N/A"}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    <div className="pagination">
                        <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                        <span> Page {currentPage} of {totalPages} </span>
                        <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
                        <button className="export-button" onClick={exportTicketBuyersToCSV}>📂 Export Ticket Buyers CSV</button>
                    </div>
                </div>
            </div>
            <div className="chart-section">
                <h2>Revenue Over Time</h2>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analyticsData.revenueData} >
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={(value) => `${value.toFixed(2)} JOD`} />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" stroke="#007bff" />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="chart-section">
                <h2>Daily Ticket Sales</h2>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analyticsData.dailySales}>
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="tickets" stroke="#ff7300" />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="ticket-sales-container">
                <div className="ticket-sales-title">
                    <h2>Event Ticket Sales Progress</h2>
                </div>
                <div className="ticket-sales-cards">
                    {ticketSalesData.map((eventData, index) => (
                        <div key={index} className="ticket-sales-card">
                            <h3>{eventData.eventName}</h3>
                            <p>{eventData.ticketsSold} / {eventData.totalTickets} Tickets Sold</p>
                            <div className="progress-bar">
                                <div className="progress-fill" style={{ width: `${(eventData.ticketsSold / eventData.totalTickets) * 100}%` }}></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="chart-section">
                <h2>Event Data Breakdown</h2>
                <div className="pie-charts-container">
                    <div className="pie-chart">
                        <h3>Ticket Sales Distribution</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie data={analyticsData.revenueData} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={100} fill="#8884d8" label={renderCustomizedLabel}>
                                    {analyticsData.revenueData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={["#6050dc", "#d52db7", "#ff2e7e", "#ff6b45", "#FF33A1"][index % 5]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => `${value.toFixed(2)} JOD`} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="pie-chart">
                        <h3>User Activity</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie data={[{ name: "Active Users", value: userStats.active }, { name: "Inactive Users", value: userStats.inactive }]} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} fill="#00C49F" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                                    <Cell key="active" fill="#00C49F" />
                                    <Cell key="inactive" fill="#FF8042" />
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="pie-chart">
                        <h3>Tickets Sold by Class</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie data={analyticsData.ticketSales} dataKey="tickets" nameKey="name" cx="50%" cy="50%" outerRadius={100} fill="#FFBB28" label={renderCustomizedLabel}>
                                    {analyticsData.ticketSales.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={["#cbba0e", "#b40c0c", "#ffa32f", "#f54f52", "#FF5733"][index % 5]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => `${value.toFixed(2)} Tickets`} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="pie-chart">
                        <h3>Revenue by Ticket Class</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            {analyticsData.ticketClassRevenue.length > 0 ? (
                                <PieChart>
                                    <Pie data={analyticsData.ticketClassRevenue} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={100} fill="#FF5733" label={renderCustomizedLabel}>
                                        {analyticsData.ticketClassRevenue.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={["#0707c4", "#1f75fe", "#74bbfb", "#cadcf1", "#17a2b8"][index % 5]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => `${value.toFixed(2)} JOD`} />
                                    <Legend />
                                </PieChart>
                            ) : (
                                <p>No data available</p>
                            )}
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default Analytics;
